import { combineReducers } from 'redux';

import {
  ADD_ALL_EXCLUDED_RANGE,
  ADD_EXCLUDED_RANGE,
  APPROVE_BILLING_DETAILS,
  ASSIGN_GLOSSARY,
  CANCEL_TABLE_SELECTION,
  ORDERING_DONE,
  ORDERING_IN_PROGRESS,
  PROCESSING_FILE_FINISHED,
  REMOVE_COLUMN_MAPPING,
  REMOVE_EXCLUDED_COLUMNS,
  REMOVE_EXCLUDED_RANGE,
  REMOVE_FILE_TO_UPLOAD,
  REMOVE_GLOSSARY,
  REMOVE_TARGET_COLUMN_MAPPING,
  RETRY_FILES_TO_UPLOAD,
  SET_COLUMN_MAPPING,
  SET_FILE_DATA,
  SET_FILE_TO_UPLOAD,
  SET_FILES_UPLOADED,
  SET_FILTERED_CONTENT,
  SET_METADATA,
  SET_TARGET_COLUMN_MAPPING,
  UPDATE_COLUMN_MAPPING,
  UPDATE_CONTENT_FLOW,
  UPDATE_CSV_DELIMITER,
  UPDATE_EXCLUDED_RANGES,
  UPDATE_INPUT_FIELD,
  UPDATE_ORDER_DETAILS,
  UPDATE_SELECTED_CELL,
  UPDATE_SELECTED_FILE,
  UPDATE_SELECTED_FILE_INDEX,
  UPDATE_SIDEBAR_FLOW,
  UPDATE_TABLE_SELECTED_RANGE,
  UPDATE_TARGET_COLUMN_MAPPING,
  UPDATE_UPLOADING_ERROR,
  UPDATE_VIEW_COORDS,
  UPDATE_WORD_COUNT
} from './_actions';

import { CONTENT_FLOWS, SIDEBAR_FLOWS } from './_consts';

import { DeepSet } from 'utils/DeepSet';

const INITIAL_SIDEBAR_FLOW = SIDEBAR_FLOWS.INPUTS;

export const staticData = (state = {}) => state;

// inputs initialState set in App.jsx
export const inputs = (state = {}, { type, field, value, fileData }) => {
  if (type === UPDATE_INPUT_FIELD)
    return {
      ...state,
      [field]: value
    };
  if (type === SET_FILE_DATA)
    return {
      ...state,
      name: fileData.fileName,
      sourceLanguageCode:
        fileData.sourceLanguageCode == 'unknown'
          ? state?.sourceLanguageCode
          : fileData.sourceLanguageCode,
      targetLanguageCodes: fileData.xliffBased
        ? state.targetLanguageCodes.filter(
            (code) => code != 'tq' && code != fileData.sourceLanguageCode
          )
        : state.targetLanguageCodes,
      quality: fileData.xliffBased && fileData.quality == 'machine' ? 'translation' : state.quality
    };
  return state;
};

export const glossaries = (state = [], { type, glossary }) => {
  switch (type) {
    case ASSIGN_GLOSSARY:
      return [...new Set(state).add(glossary)];
    case REMOVE_GLOSSARY:
      return state.filter((e) => e != glossary);
    default:
      return state;
  }
};

export const sidebarFlow = (state = INITIAL_SIDEBAR_FLOW, { type, sidebarFlow, contentFlow }) => {
  if (type === UPDATE_CONTENT_FLOW && contentFlow == CONTENT_FLOWS.SELECTION)
    return INITIAL_SIDEBAR_FLOW;
  if (type === UPDATE_SIDEBAR_FLOW) return sidebarFlow;
  return state;
};

export const contentFlow = (state = CONTENT_FLOWS.SELECTION, { type, contentFlow }) => {
  if (type === UPDATE_CONTENT_FLOW) return contentFlow;
  if (type === UPDATE_UPLOADING_ERROR) return CONTENT_FLOWS.UPLOADING_ERROR;
  return state;
};

export const filesToUpload = (state = [], { type, fileToUpload, filesToUpload, fileName }) => {
  switch (type) {
    case SET_FILE_TO_UPLOAD:
      return [...state, fileToUpload];
    case RETRY_FILES_TO_UPLOAD:
      return filesToUpload;
    case REMOVE_FILE_TO_UPLOAD:
      return state.filter((file) => file.name !== fileName);
    default:
      return state;
  }
};

export const metadata = (state = [], { type, metadata }) => {
  if (type === SET_METADATA) return [...state, metadata];
  return state;
};

export const filesUploaded = (state = [], { type, filesUploaded }) => {
  if (type === SET_FILES_UPLOADED) return filesUploaded;
  return state;
};

export const columnMapping = (
  state = [
    {
      sourceColumn: '',
      targetsColumn: [{ column: '', language: '' }]
    }
  ],
  { type, columnMapping, index, target, targetIndex }
) => {
  if (type === SET_COLUMN_MAPPING) return [...state, columnMapping];

  if (type === UPDATE_COLUMN_MAPPING)
    return state.map((c, i) => (i === index ? { ...c, ...columnMapping } : c));

  if (type === REMOVE_COLUMN_MAPPING) return state.filter((_, i) => i !== index);

  if (type === SET_TARGET_COLUMN_MAPPING)
    return state.map((c, i) =>
      i === index
        ? {
            ...c,
            targetsColumn: [...c.targetsColumn, target]
          }
        : c
    );

  if (type === REMOVE_TARGET_COLUMN_MAPPING)
    return state.map((c, i) =>
      i === index ? { ...c, targetsColumn: c.targetsColumn.filter((_, j) => j !== targetIndex) } : c
    );

  if (type === UPDATE_TARGET_COLUMN_MAPPING)
    return state.map((c, i) =>
      i === index
        ? {
            ...c,
            targetsColumn: c.targetsColumn?.map((t, j) =>
              j === targetIndex ? { ...t, ...target } : t
            )
          }
        : c
    );

  return state;
};

export const filteredContent = (state = [], { type, filteredContent }) => {
  if (type === SET_FILTERED_CONTENT) return filteredContent;
  return state;
};

export const completedFileIds = (state = [], { type, completedFileId }) => {
  if (type === PROCESSING_FILE_FINISHED) return [...state, completedFileId];
  return state;
};

export const csvDelimiter = (state = '', { type, csvDelimiter }) => {
  if (type === UPDATE_CSV_DELIMITER) return csvDelimiter;
  return state;
};

// fileData example:
// {
//   contentType: "xlsx"
//   fileName: "social planning APRIL COPY.xlsx"
//   hasRowsAndColumns: true
//   id: 15
//   keys: { B1: "LENTE / uitstapjes / pasen", B3: " ", C3: " ", … }
//   processing: false
//   sourceLanguageCode: "nl"
//   totalColumns: 13
//   totalRows: 32
//   wordCount: 1540
//   xliffBased: false
// }

export const fileData = (state = [], { type, fileData }) => {
  if (type === SET_FILE_DATA) {
    return [...state, fileData];
  }
  return state;
};

// Table handling states
// ======================================================

// It determines table pagination. It is the cell from which the rest of the cells on the view are going to be rendered.
export const viewCoords = (state = { column: 1, row: 1 }, { type, column, row }) => {
  if (type === UPDATE_VIEW_COORDS) return { column, row };
  return state;
};

// There can be a single selected range that we can either exclude or cancel.
// The range can be incomplete while it is being created like {start: someCell, end: undefined}
export const selectedRange = (state = {}, { type, selectedRange }) => {
  switch (type) {
    case ADD_EXCLUDED_RANGE:
    case ADD_ALL_EXCLUDED_RANGE:
    case CANCEL_TABLE_SELECTION:
      return {};
    case UPDATE_TABLE_SELECTED_RANGE:
      return selectedRange;
    default:
      return state;
  }
};

// Normally it represents the last clicked cell
// When clicking over an empty cell with no initialised selectedRange will define the start of the range
// it will define the end if the selectedRange already has a start
// When clicking over a excluded cell it will be used to determine selected excludedRanges
export const selectedCell = (state = {}, { type, column, row }) => {
  switch (type) {
    case UPDATE_SELECTED_CELL:
      return { column, row };
    case ADD_EXCLUDED_RANGE:
    case ADD_ALL_EXCLUDED_RANGE:
    case CANCEL_TABLE_SELECTION:
      return {};
    default:
      return state;
  }
};

export const selectedFile = (state = {}, { type, file }) => {
  switch (type) {
    case UPDATE_SELECTED_FILE:
      return file;
    default:
      return state;
  }
};

export const selectedFileIndex = (state = 0, { type, index }) => {
  switch (type) {
    case UPDATE_SELECTED_FILE_INDEX:
      return index;
    default:
      return state;
  }
};

// Ranges that wont be part of the translation request
export const excludedRanges = (
  state = [],
  { type, range, rangeAll, index, indexes, excludedRanges }
) => {
  switch (type) {
    case UPDATE_EXCLUDED_RANGES:
      return excludedRanges;
    case ADD_EXCLUDED_RANGE:
      return [...new DeepSet(state).add(range)];
    case ADD_ALL_EXCLUDED_RANGE:
      return [...new DeepSet(state).addAll(rangeAll)];
    case REMOVE_EXCLUDED_RANGE:
      return state.filter((_, i) => i != index);
    case REMOVE_EXCLUDED_COLUMNS:
      return state.filter(
        (r) => !indexes.includes(r.start.column) && !indexes.includes(r.end.column)
      );
    default:
      return state;
  }
};

const table = combineReducers({
  excludedRanges,
  viewCoords,
  selectedCell,
  selectedRange
});

// ======================================================
// (end) Table handling states

export const uploadingError = (state = '', { type, uploadingError }) => {
  if (type === UPDATE_UPLOADING_ERROR) return uploadingError;
  return state;
};

export const orderDetails = (state = {}, { type, orderDetails, contentFlow, sidebarFlow }) => {
  if (type === UPDATE_CONTENT_FLOW && contentFlow == CONTENT_FLOWS.SELECTION) return {};
  // if (type === UPDATE_SIDEBAR_FLOW && sidebarFlow != SIDEBAR_FLOWS.ORDER_DETAILS) return {};
  if (type === UPDATE_ORDER_DETAILS) return orderDetails;
  return state;
};

export const wordCount = (state = null, { type, wordCount, contentFlow }) => {
  if (type === UPDATE_CONTENT_FLOW && contentFlow == CONTENT_FLOWS.SELECTION) return null;
  if (type === UPDATE_WORD_COUNT) return wordCount;
  return state;
};

export const ordering = (state = false, { type }) => {
  if (type === ORDERING_IN_PROGRESS) return true;
  if (type === ORDERING_DONE) return false;
  return state;
};

export const wouldNeedDetails = (state = true, { type }) => {
  if (type === APPROVE_BILLING_DETAILS) return false;
  return state;
};

export default combineReducers({
  staticData,
  contentFlow,
  csvDelimiter,
  fileData,
  filesToUpload,
  metadata,
  filesUploaded,
  columnMapping,
  filteredContent,
  completedFileIds,
  inputs,
  glossaries,
  orderDetails,
  ordering,
  sidebarFlow,
  table,
  uploadingError,
  wordCount,
  wouldNeedDetails,
  selectedFile,
  selectedFileIndex
});
